/**
 * Where a session works (RFC 0277 3c, S14), in its header.
 *
 * A device icon, then the folder and the branch. Clicking opens a card that
 * says the same at length and holds the moves: into another worktree of this
 * project, into a new one started from the current branch, onto another
 * device, or `check out here` when the branch lives elsewhere. The composer
 * carries none of this: where a session works is a fact about the session,
 * not about the next message.
 *
 * Draws nothing when there is nothing to say: no git here and no branch.
 */

import {
  Box,
  Check,
  ChevronDown,
  Cloud,
  Folder,
  GitBranch,
  Laptop,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { CodeWorktree } from '../../services/code/worktrees'

export interface WhereDevice {
  deviceId: string
  name: string
  kind?: string
}

/** A VM reads as a cloud; everything else is a machine on a desk. */
const isCloud = (d: WhereDevice | undefined) =>
  !!d && (d.kind === 'vm' || d.kind === 'cloud')

export interface WhereProject {
  projectId: string
  name: string
  disabled?: boolean
}

export function WhereChip({
  project,
  projects = [],
  onSelectProject,
  devices,
  selectedDeviceId,
  trees,
  branch,
  sharedWith = 0,
  onSelectDevice,
  onSelectWorktree,
  onNewWorktree,
  onCheckOutHere,
  opens = 'down',
  otherSources,
  addedProjects,
}: {
  /** The project the session belongs to, first in the card. */
  project?: WhereProject
  /** Where it could move, offered only before the first message. */
  projects?: WhereProject[]
  onSelectProject?: (projectId: string) => void
  devices: WhereDevice[]
  selectedDeviceId: string | null
  /** The project's checkouts on this device. */
  trees: CodeWorktree[]
  /** The session's branch; absent means the main checkout. */
  branch: string | null
  /** Other sessions on the same branch. */
  sharedWith?: number
  onSelectDevice?: (deviceId: string) => void
  onSelectWorktree?: (tree: CodeWorktree) => void
  /** A new worktree, on a new branch started from `from`. */
  onNewWorktree?: (from: string) => Promise<void>
  onCheckOutHere?: () => Promise<void>
  /** Down from a header, up from a composer at the foot of the window. */
  opens?: 'down' | 'up'
  /** The session's other sources, each with its checkout (RFC 0362 3b). */
  otherSources?: React.ReactNode
  /** The projects the session added, and adding one (RFC 0362 3c). */
  addedProjects?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const device =
    devices.find((d) => d.deviceId === selectedDeviceId) ??
    (devices.length === 1 ? devices[0] : undefined)
  const tree = branch
    ? trees.find((t) => t.branch === branch)
    : trees.find((t) => t.main)
  const shownBranch = branch ?? tree?.branch ?? null
  // The branch lives on another device: it can be continued here.
  const elsewhere = !!branch && trees.length > 0 && !tree
  if (!shownBranch && trees.length === 0) return null

  const Icon = isCloud(device) ? Cloud : Laptop
  const others = trees
    .filter((t) => t.branch && t !== tree)
    .sort((a, b) =>
      a.main === b.main
        ? String(a.branch).localeCompare(String(b.branch))
        : a.main
          ? -1
          : 1,
    )

  const run = (label: string, act: () => Promise<void>) => {
    setBusy(label)
    setError(null)
    act().then(
      () => {
        setBusy(null)
        setOpen(false)
      },
      (e: unknown) => {
        setBusy(null)
        setError(e instanceof Error ? e.message : String(e))
      },
    )
  }

  return (
    // Gives up width before anything else in the header: the folder goes
    // first, then the branch, down to the icon alone. The card has it all.
    <div className="relative min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={[device?.name, tree?.name, shownBranch]
          .filter(Boolean)
          .join(' · ')}
        aria-label={device?.name ?? 'Where this session works'}
        className="flex min-w-0 max-w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-muted-foreground text-xs hover:bg-muted hover:text-foreground"
      >
        <Icon className="size-3.5 shrink-0" strokeWidth={1.75} />
        {tree ? (
          <>
            <span className="min-w-0 shrink-[8] truncate">{tree.name}</span>
            <span className="shrink-0 text-muted-foreground/50">/</span>
          </>
        ) : null}
        <span className="min-w-0 truncate">{shownBranch}</span>
        {elsewhere ? (
          <span className="shrink-0 text-amber-500">· not here</span>
        ) : null}
        <ChevronDown className="size-3 shrink-0" strokeWidth={2.25} />
      </button>
      {open ? (
        <div
          className={`absolute z-30 w-80 ${opens === 'up' ? 'bottom-full left-0 mb-1' : 'top-full right-0 mt-1'} overflow-hidden rounded-xl border border-border bg-background py-1.5 text-sm shadow-lg`}
        >
          {project ? (
            <Line icon={<Box className="size-3.5" strokeWidth={1.75} />}>
              {project.name}
              <Sub>project</Sub>
            </Line>
          ) : null}
          <Line icon={<Icon className="size-3.5" strokeWidth={1.75} />}>
            {device?.name ?? 'Any device'}
            <Sub>{device ? 'online' : 'whichever has this project'}</Sub>
          </Line>
          {tree ? (
            <Line icon={<Folder className="size-3.5" strokeWidth={1.75} />}>
              {tree.name}
              <Sub>{tree.main ? 'main checkout' : 'worktree'}</Sub>
            </Line>
          ) : null}
          {shownBranch ? (
            <Line icon={<GitBranch className="size-3.5" strokeWidth={1.75} />}>
              {shownBranch}
              {sharedWith > 0 ? (
                <Sub>
                  shared with {sharedWith} other{' '}
                  {sharedWith === 1 ? 'session' : 'sessions'}
                </Sub>
              ) : null}
            </Line>
          ) : null}

          {elsewhere && onCheckOutHere ? (
            <Move
              label="Check out here"
              sub={`Fetch ${branch} into a worktree on this device.`}
              busy={busy}
              onClick={() => run('Check out here', onCheckOutHere)}
            />
          ) : null}
          {!elsewhere && shownBranch && onNewWorktree ? (
            <Move
              label="Move to a new worktree"
              sub={`Its own folder, on a new branch from ${shownBranch}.`}
              busy={busy}
              onClick={() =>
                run('Move to a new worktree', () => onNewWorktree(shownBranch))
              }
            />
          ) : null}
          {others.length > 0 && onSelectWorktree ? (
            <>
              <Label>Move into</Label>
              <div className="max-h-56 overflow-y-auto">
                {others.map((t) => (
                  <button
                    key={t.name}
                    type="button"
                    onClick={() => {
                      onSelectWorktree(t)
                      setOpen(false)
                    }}
                    className="flex w-full flex-col items-start px-3 py-1.5 pl-9 text-left hover:bg-muted"
                  >
                    <span className="break-all text-foreground text-xs">
                      {t.branch}
                    </span>
                    <Sub>{t.main ? 'main checkout' : t.name}</Sub>
                  </button>
                ))}
              </div>
            </>
          ) : null}
          {otherSources ? (
            <>
              <Label>Other sources</Label>
              {otherSources}
            </>
          ) : null}
          {addedProjects ? (
            <>
              <Label>Also works in</Label>
              {addedProjects}
            </>
          ) : null}
          {onSelectProject && projects.length > 1 ? (
            <>
              <Label>Start in</Label>
              {projects.map((p) => (
                <button
                  key={p.projectId}
                  type="button"
                  disabled={p.disabled}
                  onClick={() => {
                    onSelectProject(p.projectId)
                    setOpen(false)
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted disabled:opacity-50"
                >
                  <Check
                    className={`size-3.5 ${p.projectId === project?.projectId ? 'opacity-100' : 'opacity-0'}`}
                    strokeWidth={2.5}
                  />
                  <span>{p.name}</span>
                </button>
              ))}
            </>
          ) : null}
          {devices.length > 1 && onSelectDevice ? (
            <>
              <Label>Runs on</Label>
              {devices.map((d) => {
                const DeviceIcon = isCloud(d) ? Cloud : Laptop
                return (
                  <button
                    key={d.deviceId}
                    type="button"
                    onClick={() => {
                      onSelectDevice(d.deviceId)
                      setOpen(false)
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-muted"
                  >
                    <Check
                      className={`size-3.5 ${d.deviceId === selectedDeviceId ? 'opacity-100' : 'opacity-0'}`}
                      strokeWidth={2.5}
                    />
                    <DeviceIcon className="size-3.5" strokeWidth={1.75} />
                    <span>{d.name}</span>
                  </button>
                )
              })}
            </>
          ) : null}
          {error ? (
            <p className="px-3 pt-1 text-[11px] text-red-500">{error}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function Line({
  icon,
  children,
}: {
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-2.5 px-3 py-1.5 text-foreground text-xs">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <span className="flex min-w-0 flex-col break-all">{children}</span>
    </div>
  )
}

function Sub({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] text-muted-foreground break-normal">
      {children}
    </span>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-1 border-border border-t px-3 pt-2 pb-0.5 text-[11px] text-muted-foreground">
      {children}
    </div>
  )
}

function Move({
  label,
  sub,
  busy,
  onClick,
}: {
  label: string
  sub: string
  busy: string | null
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={busy === label}
      onClick={onClick}
      className="mt-1 flex w-full flex-col items-start border-border border-t px-3 py-1.5 pl-9 text-left text-xs hover:bg-muted"
    >
      <span className="text-foreground">
        {busy === label ? `${label}…` : label}
      </span>
      <Sub>{sub}</Sub>
    </button>
  )
}
